import { useCallback, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';

import { Theme } from '@app/theme';

import {
  ModeratedTaskSetting,
  Participation,
  ParticipationMutationOptions,
  ParticipationUpdateData,
  usePaginatedAvailableDatesForReschedule,
  useReschedule,
  useSession,
  useTaskMeeting,
  useTaskSetting,
} from '@app/api';
import { MutationOptions } from 'react-query';

import {
  CenteredLoader,
  CenteredModal,
  InformationBox,
  useParticipationDisplayName,
  useToast,
} from 'components';
import { useTranslation } from 'react-i18next';
import { MeetingDateForm } from './MeetingDateForm';
import moment from 'moment';

const useStyles = createUseStyles((theme: Theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 3 + 'px',
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose: (isCancelled: boolean) => any;
  participation?: Participation;
  mutationOptions?: ParticipationMutationOptions;
}

export const RescheduleMeetingModal = ({
  participation,
  isOpen,
  onRequestClose,
  mutationOptions,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();
  const { t } = useTranslation();

  const { data: session } = useSession(participation?.sessionId);
  const { data: meeting } = useTaskMeeting(participation?.taskId);
  const { data: taskSetting } = useTaskSetting<ModeratedTaskSetting>(
    session?.taskSettingId,
  );
  const paginatedAvailableDates = usePaginatedAvailableDatesForReschedule(
    participation?.taskId,
  );

  const { mutateAsync: reschedule } = useReschedule(
    participation?.id,
    mutationOptions,
  );

  const name = useParticipationDisplayName(participation);

  const initialValues = useMemo(() => {
    if (!meeting?.start) {
      return undefined;
    }
    if (moment(meeting.start).isBefore(moment())) {
      return { date: '' };
    }
    return {
      date: moment(meeting.start).toISOString(),
    };
  }, [meeting]);

  const submit = useCallback(
    async (values: any) => {
      try {
        if (!participation) {
          return;
        }
        await reschedule(values);
        toast.success(t('sessions.details.appointmentBooking.toaster'));
        onRequestClose(false);
      } catch (e) {
        toast.exception(e);
      }
    },
    [participation, reschedule, toast, t, onRequestClose],
  );

  if (!participation) {
    return null;
  }
  return (
    <CenteredModal
      isOpen={isOpen}
      title={t('sessions.details.appointmentBooking.rescheduleTitle', {
        participant: name,
      })}
      onRequestClose={() => onRequestClose(true)}
    >
      <div className={classes.content}>
        <InformationBox
          title={t('sessions.details.appointmentBooking.warn.rescheduleTitle')}
          text={t('sessions.details.appointmentBooking.warn.rescheduleMessage')}
          variant="danger"
        />
        {taskSetting && initialValues ? (
          <MeetingDateForm
            duration={taskSetting?.appointmentDuration}
            onRequestClose={() => onRequestClose(true)}
            initialValues={initialValues}
            paginatedAvailableDates={paginatedAvailableDates}
            onSubmit={submit}
          />
        ) : (
          <CenteredLoader />
        )}
      </div>
    </CenteredModal>
  );
};
